import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { SingleDatePicker } from 'react-dates';
import { ICON_AFTER_POSITION } from 'react-dates/constants';
import styled, { css } from 'styled-components';

import { colors, Theme } from '../../../core';
import Icon from '../Icon';
import Label from '../Label';
import Text from '../Text';
import YearMonthPicker from './YearMonthPicker';

export interface DatePickerProps {
  value: Moment | null;
  onChange: (date: Moment | null) => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  animatedLabel?: string;
  name?: string;
  onBlur?: () => void;
  onMonthChange?: (date: Moment | null) => void;
  onYearChange?: (date: Moment | null) => void;
  width?: string;
  yearPicker?: any;
  numberOfMonths?: number;
  renderMonthContents?: any;
  renderDayContents?: any;
  futureYears?: number;
  isOutsideRange?: any;
  showRequired?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  errorMessage?: string;
  variant?: 'primary' | 'secondary';
}

export interface StyledDatePickerProps {
  className?: string;
  name?: string;
  onBlur?: () => void;
  width?: string;
  yearPicker?: any;
  hasEnteredText: boolean;
  isError?: boolean;
  variant?: 'primary' | 'secondary';
}

const baseSmallLabelStyles = css`
  ${({ theme }) => `
    font-size: 12px;
    top: 0;
    color: ${theme.fontColor || colors.ash};
  `}
`;

const PrevIcon = styled(Icon).attrs(({ theme }) => ({
  type: 'caret-left',
  color: theme.datePickerIconColor || colors.primary,
}))`
  position: absolute;
  top: 24px;
  left: 22px;
  cursor: pointer;
  &:hover {
    path {
      fill: ${({ theme }) => theme.datePickerIconColorHover || colors.primaryHover};
    }
  }
`;

const NextIcon = styled(Icon).attrs(({ theme }) => ({
  type: 'caret-right',
  color: theme.datePickerIconColor || colors.primary,
}))`
  position: absolute;
  top: 24px;
  right: 22px;
  cursor: pointer;
  &:hover {
    path {
      fill: ${({ theme }) => theme.datePickerIconColorHover || colors.primaryHover};
    }
  }
`;

const MiddlePrevIcon = styled(PrevIcon)`
  left: 23px;
  &:hover {
    path {
      fill: ${({ theme }) => theme.datePickerIconColorHover || colors.primaryHover};
    }
  }
`;

const MiddleNextIcon = styled(NextIcon)`
  right: 23px;
  &:hover {
    path {
      fill: ${({ theme }) => theme.datePickerIconColorHover || colors.primaryHover};
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledDatePicker = styled.div<StyledDatePickerProps>`
  width: ${({ width }) => width || '100%'};
  position: relative;

  ${({ hasEnteredText }) =>
    hasEnteredText &&
    css`
      ${AnimatedLabel} {
        ${baseSmallLabelStyles};
      }
    `}

  .SingleDatePicker {
    display: block;
  }

  .SingleDatePicker_picker {
    margin-top: ${({ theme }) => theme.datePickerTop || 0};
  }

  .SingleDatePickerInput {
    overflow: hidden;
    display: flex;
    background-color: ${({ theme, variant }) =>
      variant === 'secondary' && theme.datePickerShowOnlyBottomBorder
        ? 'transparent'
        : theme.textInputBackgroundColor || colors.white};

    &:hover {
      box-shadow: ${({ theme }) =>
        theme.datePickerHoverShadow || '0 2px 4px 0 rgba(217, 217, 217, 0.5)'};
    }

    &__withBorder {
      border-color: ${({ isError, theme, variant }) =>
        isError
          ? theme.errorColor
          : (variant === 'primary'
              ? theme.textInputBorderColor
              : theme.textInputSecondaryBorderColor) || colors.smoke};
      border-radius: ${({ theme }) => theme.borderRadius || '3px'};

      ${({ theme, variant }) =>
        variant === 'secondary' &&
        theme.datePickerShowOnlyBottomBorder &&
        `
          border-top: 0;
          border-left: 0;
          border-right: 0;
          border-radius: 0;
      `}
    }

    &__disabled {
      background-color: ${({ theme, variant }) =>
        variant === 'secondary' && theme.datePickerShowOnlyBottomBorder
          ? 'transparent'
          : theme.textInputDisabledBackgroundColor || colors.pearl};
      border-color: ${({ theme }) => theme.textInputDisabledBorderColor || colors.cloud};

      &:hover {
        box-shadow: none;
      }

      .SingleDatePickerInput_calendarIcon {
        cursor: default;
      }
    }
  }

  .SingleDatePickerInput_calendarIcon {
    line-height: 1;
    margin: 0;
    padding: ${({ theme }) => theme.datePickerCalendarIconPadding || '6px 10px'};
    cursor: pointer;

    svg {
      cursor: pointer;
    }
  }

  .DateInput {
    flex: 1;
    background-color: transparent;

    &__disabled {
      background-color: transparent;
    }
  }

  .DateInput_input {
    height: ${({ theme }) => theme.datePickerInputHeight || '34px'};
    font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
    font-size: ${({ theme }) => theme.fontSize || '14px'};
    font-weight: ${({ theme }) => theme.fontWeight || 600};
    line-height: 20px;
    padding: ${({ theme }) => theme.datePickerPadding || '7px 10px 5px'};
    color: ${({ theme }) => theme.datePickerColor || colors.ash};
    background-color: transparent;

    &::placeholder {
      color: ${({ theme }) => theme.datePickerPlaceholderColor || colors.smoke};
    }

    &__focused {
      background-color: transparent;
      border-bottom-color: ${({ theme }) => theme.datePickerFocusedBorderColor || colors.primary};
    }

    &__disabled {
      background-color: ${({ theme, variant }) =>
        variant === 'secondary' && theme.datePickerShowOnlyBottomBorder
          ? 'transparent'
          : theme.textInputDisabledBackgroundColor || colors.pearl};
      color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
      font-style: normal;
      -webkit-text-fill-color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
      opacity: 1;
    }

    &:read-only {
      cursor: pointer;
    }
  }

  .CalendarDay__blocked_out_of_range {
    border: 0;
    color: ${({ theme }) => theme.textInputDisabledColor || colors.primary};
  }

  .CalendarDay__selected {
    background-color: ${({ theme }) => theme.datePickerActiveBackgroundColor || colors.primary};
    border-color: ${({ theme }) => theme.datePickerActiveBorderColor || colors.primary};
    color: ${({ theme }) => theme.datePickerActiveColor || colors.white};
  }

  .CalendarDay:hover {
    background-color: ${({ theme }) => theme.datePickerHoverBackgroundColor || colors.primary};
    border-color: ${({ theme }) => theme.datePickerHoverBorderColor || colors.primary};
    color: ${({ theme }) => theme.datePickerHoverColor || colors.white};
  }

  ${({ yearPicker }: any) =>
    yearPicker &&
    css`
      .CalendarMonth_caption {
        padding: 8px 0;
      }

      .DayPicker_weekHeaders {
        display: none;
      }
    `}
`;

const StyledIcon = styled(Icon).attrs(({ theme }) => ({
  color: theme.datePickerIconColor || colors.primary,
}))``;

const DisabledIcon = styled(Icon).attrs(({ theme }) => ({
  color: theme.datePickerIconDisabledColor || colors.primaryDisabled,
}))`
  cursor: default !important;
`;

const StyledLabel = styled(Label)<{ variant?: 'primary' | 'secondary' }>`
  ${({ theme, variant }) =>
    theme.name !== Theme.theme1 &&
    variant === 'secondary' &&
    `
      font-size: 12px;
      font-weight: 400;
      padding: 0 16px;
      margin: 0;
      position: absolute;
      top: -8px;
      line-height: 1.5;
    `}
`;

const AnimatedLabel = styled(Label)`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 2;
  transition: top 300ms ease-in-out, font-size 300ms ease-in-out, opacity 300ms ease-in-out;
  color: ${({ theme }) => theme.textInputColor || colors.ash};
  z-index: ${({ theme }) => theme.name !== Theme.theme1 && -1};
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
  line-height: 1.5;
`;

/**
 * DatePicker style wrapper around react-dates library. The implementer of this component must
 * manage date state internally using onDateChange to update it.
 */
const DatePicker = ({
  className,
  disabled,
  label,
  animatedLabel,
  name,
  onBlur,
  onChange,
  onMonthChange,
  onYearChange,
  width,
  value,
  yearPicker,
  numberOfMonths,
  renderMonthContents,
  renderDayContents,
  futureYears,
  showRequired,
  errorMessage,
  variant = 'primary',
  ...rest
}: DatePickerProps) => {
  const [isFocused, setFocused] = useState(false);
  const [hasEnteredText, setHasEnteredText] = useState(false);

  useEffect(() => {
    if (isFocused || value) {
      setHasEnteredText(true);
    } else {
      setHasEnteredText(false);
    }
  }, [isFocused, value]);

  return (
    <StyledDatePicker
      className={className}
      name={name}
      onBlur={onBlur}
      width={width}
      yearPicker={yearPicker}
      hasEnteredText={hasEnteredText}
      isError={!!errorMessage}
      variant={variant}
    >
      {label && (
        <StyledLabel variant={variant}>
          {label}
          {showRequired && '*'}
        </StyledLabel>
      )}
      <InputWrapper>
        <SingleDatePicker
          {...(rest as any)}
          customInputIcon={
            disabled ? <DisabledIcon type="input-calendar" /> : <StyledIcon type="input-calendar" />
          }
          date={value ? moment(value) : null}
          disabled={disabled}
          focused={isFocused}
          hideKeyboardShortcutsPanel
          inputIconPosition={ICON_AFTER_POSITION}
          navNext={futureYears ? <MiddleNextIcon /> : <NextIcon />}
          navPrev={futureYears ? <MiddlePrevIcon /> : <PrevIcon />}
          numberOfMonths={numberOfMonths || (yearPicker ? 1 : 2)}
          onDateChange={onChange}
          onPrevMonthClick={onMonthChange}
          onNextMonthClick={onMonthChange}
          onFocusChange={({ focused }: any) => setFocused(focused)}
          verticalSpacing={0}
          renderDayContents={renderDayContents}
          renderMonthElement={(props) => {
            return renderMonthContents ? (
              renderMonthContents
            ) : yearPicker ? (
              <YearMonthPicker
                futureYears={futureYears}
                onYearChange={onYearChange}
                onMonthChange={onMonthChange}
                {...props}
              />
            ) : (
              <strong>{moment().month(props.month.month()).format('MMMM')}</strong>
            );
          }}
        />
        {animatedLabel && (
          <AnimatedLabel>
            {animatedLabel}
            {showRequired && '*'}
          </AnimatedLabel>
        )}
      </InputWrapper>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </StyledDatePicker>
  );
};

export default DatePicker;
